import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useProjectsStore } from "@/stores/projects.store";
import type {
  ReadProjectDto,
  ReadProjectWithRelationsDto
} from "@package/types";
import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import type { FormEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (project: ReadProjectWithRelationsDto) => Promise<void> | void;
  navigateOnCreated?: boolean;
}

export function CreateProjectDialog({
  open,
  onOpenChange,
  onCreated,
  navigateOnCreated = true
}: CreateProjectDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const addProject = useProjectsStore((s) => s.addProject);

  const [name, setName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isCreating) {
      setName("");
    }
    onOpenChange(nextOpen);
  };

  const createProject = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;

    try {
      setIsCreating(true);
      const project = await api.post<ReadProjectDto>("/projects", {
        name: name.trim()
      });
      const projectWithRelations: ReadProjectWithRelationsDto = {
        ...project,
        chats: [],
        files: []
      };

      addProject(projectWithRelations);
      await onCreated?.(projectWithRelations);
      setName("");
      onOpenChange(false);
      if (navigateOnCreated) {
        navigate({
          to: "/projects/$projectId",
          params: { projectId: project.id }
        });
      }
    } catch (error) {
      console.error("Failed to create project:", error);
      toast.error(t("projects.createError"));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={createProject} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t("projects.create")}</DialogTitle>
            <DialogDescription>
              {t("projects.createDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="project-name">{t("projects.name")}</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("projects.namePlaceholder")}
              disabled={isCreating}
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={isCreating || !name.trim()}>
              {isCreating ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("projects.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
